<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import {
  Language,
  StepProposalResult,
  StepProposalsFromDocxMutation,
  StepProposalsFromDocxMutationVariables,
} from '@/generated/graphql'
import { useMutation } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { reactive, ref } from 'vue'
import { v4 } from 'uuid'
import { PickedUpload } from '@/components/upload/UploadPicker.vue'
import StepProposalsProgress from '@/components/step/StepProposalsProgress.vue'
import useVuelidate from '@vuelidate/core'
import { required } from '@/validation'
import { stepProposalFragments } from '@/components/step/graphql'
import LanguagePicker from '@/components/input/LanguagePicker.vue'
import TextAreaField from '@/components/input/TextAreaField.vue'

const props = defineProps<{
  upload: PickedUpload
}>()
const emit = defineEmits<{
  result: [value: StepProposalResult]
}>()
const { t } = useI18n()

type Model = {
  language: Language
  additionalPrompt?: string
}
const model = reactive<Model>({
  language: Language.Nl,
  additionalPrompt: '',
})
const validation = useVuelidate<Model>({ language: { required } }, model)

const mutation = useMutation<StepProposalsFromDocxMutation, StepProposalsFromDocxMutationVariables>(
  gql`
    mutation StepProposalsFromDocx($command: CreateStepProposalsFromDOCX!) {
      product {
        createStepProposalsFromDOCX(command: $command) {
          ...StepProposalResultDetails
        }
      }
    }

    ${stepProposalFragments}
  `,
)

const loadingRequestId = ref<string>()
const doProposals = () => {
  validation.value.$touch()
  if (validation.value.$invalid) {
    return
  }

  loadingRequestId.value = v4()
  mutation
    .mutate({
      command: {
        id: loadingRequestId.value as string,
        fromUploadId: props.upload.id,
        language: model.language,
        additionalPrompt: model.additionalPrompt?.trim() || undefined,
      },
    })
    .then((response) => {
      emit('result', response?.data?.product.createStepProposalsFromDOCX as StepProposalResult)
    })
    .finally(() => {
      setTimeout(() => (loadingRequestId.value = undefined), 3_000)
    })
}
</script>

<template>
  <div v-if="!loadingRequestId">
    <language-picker
      v-model="model.language"
      :label="t('component.stepProposalsFromDocxLoader.inputLanguage')"
      :validation="validation.language"
      required
    />
    <text-area-field
      v-model="model.additionalPrompt"
      :label="t('component.stepProposalsFromDocxLoader.additionalPrompt')"
      :hint="t('component.stepProposalsFromDocxLoader.additionalPromptHint')"
      rows="3"
    />
    <div class="text-center">
      <v-btn color="primary" class="mt-5" :loading="mutation.loading.value" @click="doProposals">
        {{ t('component.stepProposalsFromFileDialog.buttonCreateProposals') }}
      </v-btn>
    </div>
  </div>
  <step-proposals-progress v-if="!!loadingRequestId" :id="loadingRequestId" />
</template>

<style scoped lang="scss"></style>
